import posthog from 'posthog-js'
import { useRouter } from 'next/router'
import { useCallback, useRef, useState } from 'react'
import HomeLayout from 'layouts/HomeLayout'
import { PageHeader } from '@/components/PageHeader'
import { PageSection } from '@/components/PageSection'
import { supabaseClient } from '@/common/supabase'
import { PATHS } from '@/common/const'
import { useUser } from '@supabase/auth-helpers-react'
import { ButtonWithIcon, InlineInput } from '@/components/input'
import { useInput } from 'hooks/useInput'
import { Country, findCountryByProp } from '@/common/countries'
import { PhoneInput, PhoneInputData } from '@/components/PhoneInput'
import MainContainer from '@/components/MainContainer'
import {
  DisplayCheckIn,
  ListNotesWithGracePeriod,
} from '@/components/DisplayCheckIn'
import { SectionFooter } from '@/components/SectionFooter'
import { handleAPIRequest } from '@/common/request'
import { updateAccountData } from '@/common/account'
import { toast } from 'react-hot-toast'

function AccountInfo() {
  const [isBusy, setBusy] = useState(false)

  const user = useUser()
  const router = useRouter()

  const name_last = useInput(user?.user_metadata?.name_last ?? '')
  const name_first = useInput(user?.user_metadata?.name_first ?? '')

  const phoneRef = useRef<PhoneInputData | null>({
    country: findCountryByProp(
      'dial_code',
      user?.user_metadata?.country_code?.toString?.() as string
    ),
    isPossible: false,
    local: user?.user_metadata?.phone,
    full_phone: user?.user_metadata?.full_phone,
  })

  const updateAccount = useCallback(async () => {
    if (
      phoneRef?.current?.full_phone?.length &&
      !phoneRef?.current?.isPossible
    ) {
      toast.error('Please provide a valid phone number')
      return
    }

    handleAPIRequest(
      updateAccountData({
        user: user,
        phone: phoneRef.current,
        name_last: name_last.value,
        name_first: name_first.value,
      }),
      {
        busyState: setBusy,
        successMessage: 'Account updated!',
        loadingMessage: 'Updating your account...',
        errorMessage:
          "Sorry, we couldn't update your account. Please try again.",
      }
    )
  }, [user?.user_metadata, name_first.value, name_last.value])

  return (
    <MainContainer width={4}>
      <div>
        <PageHeader heading="Your Account">
          <ButtonWithIcon
            onClick={() => {
              supabaseClient.auth.signOut().then(() => {
                posthog.reset(true)
                router.push(PATHS.AFTER_SIGN_OUT)
              })
            }}
          >
            Sign out
          </ButtonWithIcon>
        </PageHeader>
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3 lg:gap-8">
          <div className="grid-cols-1 lg:col-span-2 space-y-4">
            <PageSection heading="Check-in Status">
              <div className="p-4">
                <DisplayCheckIn />
              </div>
              <ListNotesWithGracePeriod />
            </PageSection>
            <PageSection heading="Your Information">
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  updateAccount()
                }}
              >
                <dl className="sm:divided-y">
                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 py-4 px-4">
                    <dt className="text-sm font-medium">Email address</dt>
                    <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                      <p>{user?.email}</p>
                    </dd>
                  </div>
                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 py-4 px-4">
                    <dt className="text-sm font-medium">Full name</dt>
                    <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                      <div className="flex justify-between">
                        <InlineInput
                          type="text"
                          {...name_first}
                          placeholder="First name"
                          autoComplete="first-name"
                        />
                        <InlineInput
                          type="text"
                          {...name_last}
                          classes="ml-0 md:ml-2"
                          placeholder="Last name"
                          autoComplete="family-name"
                        />
                      </div>
                    </dd>
                  </div>
                  <div className="sm:grid sm:grid-cols-3 sm:gap-4 py-4 px-4">
                    <dt className="text-sm font-medium">Phone number</dt>
                    <dd className="mt-1 flex items-center text-sm sm:col-span-2 sm:mt-0">
                      <PhoneInput refValue={phoneRef} />
                    </dd>
                  </div>
                </dl>
                <SectionFooter
                  leftContent={
                    <p>This information will be attached with notes.</p>
                  }
                  rightContent={
                    <ButtonWithIcon
                      type="submit"
                      color="green"
                      isDisabled={isBusy}
                      classes="mt-4 md:mt-0"
                    >
                      Update
                    </ButtonWithIcon>
                  }
                />
              </form>
            </PageSection>
            <PageSection>
              <p className="p-4 muted">
                If you'd like to delete your account and all the associated
                information, please contact us via chat or email us at
                help@backupdiary.com
              </p>
            </PageSection>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* <PageSection heading="Billing">
                  <p className="p-4 text-center">
                    You do not have any billing history yet.
                  </p>
                </PageSection> */}
          </div>
        </div>
      </div>
    </MainContainer>
  )
}

export default function Account() {
  return (
    <HomeLayout title="Your Account" requireAuth={true}>
      <AccountInfo />
    </HomeLayout>
  )
}
